import { useRoute } from "@react-navigation/native";
import { useCallback, useEffect } from "react";
import { useHierarchicalMenu } from "react-instantsearch-hooks";
import { HierarchicalMenuItem } from "instantsearch.js/es/connectors/hierarchical-menu/connectHierarchicalMenu";
import { FlatList, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";

import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";

import { fonts } from "../fonts";

const CategoryRefinement: React.FC<{ searchKey: string }> = ({ searchKey }) => {
  const route = useRoute<{ name: string; key: string; params: { category: string } }>();
  const { items, refine } = useHierarchicalMenu({
    attributes: ["categoryHierarchy.lvl0", "categoryHierarchy.lvl1"],
    separator: " - ",
    showParentLevel: true,
  });
  const dispatch = useAppDispatch();
  const rAccountType = useSelector(({ UserReducer }) => UserReducer.profile.accountType);
  const kSearchBrand = useSelector((state) => state.SearchReducer.rSearchData[searchKey]?.currentSearch ?? "");

  const aKeyExtractor = useCallback((item) => item.label, []);

  const aItemSeparatorComponent = useCallback(() => {
    return <View style={styles.P99_CategorySeparator} />;
  }, []);

  useEffect(() => {
    if (route.params?.category) {
      refine(route.params?.category);
    }
  }, [route.params?.category, refine]);

  useEffect(() => {
    if (kSearchBrand) {
      refine("");
    }
  }, [kSearchBrand, refine]);

  const aRenderItem = useCallback(
    ({ item }: { item: HierarchicalMenuItem }) => {
      return (
        <View>
          <TouchableOpacity
            disabled={rAccountType === "rep"}
            onPress={() => {
              dispatch({
                type: "UPDATE_SEARCH",
                rSearchData: {
                  [searchKey]: {
                    currentSearch: "",
                    recentSearches: undefined,
                  },
                },
              });
              refine(item.value);
            }}
          >
            <View style={styles.P99_CategoryItem}>
              <Text style={item.isRefined ? fonts.H3_Selected_Black : fonts.H3_BrandDark}>{item.label}</Text>
              <Text style={fonts.H3_Light_Black}>{` (${item.count})`}</Text>
            </View>
          </TouchableOpacity>
          {item.data && (
            <View style={styles.P99_SubcategoryContainer}>
              {item.data.map((subItem) => {
                return (
                  <TouchableOpacity
                    disabled={rAccountType === "rep"}
                    key={subItem.value}
                    onPress={() => {
                      refine(subItem.value);
                    }}
                  >
                    <View style={styles.P99_SubCategoryItem}>
                      <Text style={subItem.isRefined ? fonts.H4_Selected_Black : fonts.H4_Black}>{subItem.label}</Text>
                      <Text style={fonts.FacetCount_Black}>{subItem.count}</Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>
      );
    },
    [dispatch, rAccountType, refine, searchKey],
  );

  return items.length !== 0 ? (
    <View>
      <FlatList
        data={items}
        keyExtractor={aKeyExtractor}
        ItemSeparatorComponent={aItemSeparatorComponent}
        renderItem={aRenderItem}
      />
    </View>
  ) : (
    <View style={styles.mNoCategoryTextContainer}>
      <Text style={styles.gTableEmptyMessageText}>No category options found</Text>
    </View>
  );
};

export default CategoryRefinement;

const styles = StyleSheet.create({
  P99_CategoryItem: {
    alignItems: "center",
    flexDirection: "row",
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  P99_CategorySeparator: {
    borderBottomWidth: 0.75,
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  P99_SubCategoryItem: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  P99_SubcategoryContainer: {
    paddingLeft: 3 * kStyleConst.kSpacing,
  },
  gTableEmptyMessageText: {
    ...kFonts.bodyItalic,
    color: kColors.mediumGrey,
  },
  mNoCategoryTextContainer: {
    marginBottom: 5 * kStyleConst.kSpacing,
  },
});
